import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { adhdAppImage, stillWeRiseBanner, tableauResearch, freeDatasetGraphic } from "@/assets";

export default function Projects() {
  return (
    <div className="flex flex-col pt-10">
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-background border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-foreground leading-[1.1] tracking-tight mb-8">
            Current Projects.
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl text-balance">
            Work in progress. These are the things I'm actively building, researching, and refining across data, design, and development.
          </p>
        </div>
      </section>

      {/* Featured Project: Neurodivergence Accountability App */}
      <section className="py-24 bg-accent/10 border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="bg-white border border-border shadow-sm rounded-sm overflow-hidden flex flex-col lg:flex-row">
            <div className="lg:w-3/5 relative">
              <div className="aspect-video lg:aspect-auto lg:h-full bg-muted">
                <img 
                  src={adhdAppImage}
                  alt="Neurodivergence Accountability App" 
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary border border-border">
                Featured
              </div>
            </div>
            
            <div className="lg:w-2/5 p-10 lg:p-14 flex flex-col justify-center">
              <div className="space-y-6">
                <div className="inline-block px-3 py-1 bg-amber/10 text-secondary text-xs uppercase tracking-widest font-semibold border border-amber/20">
                  In Progress
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-serif text-foreground leading-tight">
                  Neurodivergence Accountability App
                </h2>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  A tool built around human motivation and behavioral design rather than productivity guilt — designed specifically for ADHD and neurodivergent users who need reward structures, not reminders.
                </p>
                
                <div className="pt-6 border-t border-border/40 grid grid-cols-2 gap-4">
                  <div>
                    <span className="block text-xs uppercase tracking-widest text-muted-foreground font-medium mb-1">Tags</span>
                    <span className="text-sm font-medium text-foreground">App Development, Design</span>
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-widest text-muted-foreground font-medium mb-1">Timeline</span>
                    <span className="text-sm font-medium text-foreground">Est. completion: September 2026</span>
                  </div>
                </div>
                <div className="pt-2">
                  <Link
                    href="/thrivr"
                    className="text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors"
                  >
                    View Thrivr case study →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* YouTube Embed */}
          <div className="mt-12">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-primary mb-6">Project Overview Video</h3>
            <div className="relative w-full rounded-sm overflow-hidden border border-border/40 shadow-sm" style={{ paddingBottom: "56.25%" }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/tJ2AP9F_EUk?si=Zij7XVlDnWyHxMdA"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Supporting Projects */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Still We Rise */}
            <div className="group space-y-6">
              <div className="aspect-[4/3] bg-muted overflow-hidden border border-border/40 rounded-sm">
                <img 
                  src={stillWeRiseBanner}
                  alt="Still We Rise — Empowering Neurodivergent Black Women" 
                  className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-2xl font-serif text-foreground">Still We Rise</h3>
                  <span className="text-xs font-semibold uppercase text-muted-foreground border border-border/60 px-2 py-1 rounded-sm">Currently on Hiatus</span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  A Black women's support group focused on empowering neurodivergent Black women through community, joy, hope, and shared experience. Currently on hiatus.
                </p>
                <a href="https://stillwerise.gumroad.com/" target="_blank" rel="noopener noreferrer" className="text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                  Visit Online Store →
                </a>
              </div>
            </div>

            {/* Mega Dataset for Tableau */}
            <div className="group space-y-6">
              <div className="aspect-[4/3] bg-muted overflow-hidden border border-border/40 rounded-sm">
                <img 
                  src={tableauResearch}
                  alt="Mega Dataset for Tableau — Research Findings" 
                  className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-2xl font-serif text-foreground">Mega Dataset for Tableau</h3>
                  <span className="text-xs font-semibold uppercase text-secondary border border-secondary/30 px-2 py-1 rounded-sm">Coming Soon</span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Building a large, beginner-friendly dataset with accompanying Tableau visualizations — designed to give new analysts a strong, practical foundation for data exploration.
                </p>
                <a href="https://public.tableau.com/app/profile/shateara.davis/vizzes" target="_blank" rel="noopener noreferrer" className="text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                  View Tableau Profile →
                </a>
              </div>
            </div>

            {/* Dataset Graphic / Portfolio Website */}
            <div className="group space-y-6">
              <div className="aspect-[4/3] bg-muted overflow-hidden border border-border/40 rounded-sm">
                <img
                  src={freeDatasetGraphic}
                  alt="Reputable Datasets for Data Analytics Beginners"
                  className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-2xl font-serif text-foreground">Data Analytics Learning Journey</h3>
                  <span className="text-xs font-semibold uppercase text-muted-foreground">Ongoing</span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Studying real datasets, building dashboards, and applying statistical rigor to unearth patterns in open-source data. Sharing findings publicly on Tableau and Medium.
                </p>
                <a href="https://public.tableau.com/app/profile/shateara.davis/vizzes" target="_blank" rel="noopener noreferrer" className="text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                  View on Tableau Public →
                </a>
              </div>
            </div>

            {/* Portfolio Website */}
            <div className="group space-y-6">
              <div className="aspect-[4/3] bg-plum/5 overflow-hidden border border-border/40 rounded-sm flex items-center justify-center p-12 relative">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/noise-pattern-with-subtle-cross-lines.png')] opacity-20"></div>
                <h2 className="font-serif text-5xl font-semibold tracking-tight text-plum z-10 text-center">
                  Shateara Davis<span className="text-secondary">.</span>
                </h2>
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-2xl font-serif text-foreground">Portfolio Website v2</h3>
                  <span className="text-xs font-semibold uppercase text-secondary">Live</span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  This site. Custom built with React, Tailwind, and a meticulous design system focused on typography and editorial layout. No templates.
                </p>
                <a href="https://shatearadavis.com" target="_blank" rel="noopener noreferrer" className="text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors">
                  Visit Live Site →
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lessons Learned Note */}
      <section className="py-24 bg-accent/30 border-y border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center space-y-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary">
            Lessons Learned
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-foreground leading-snug text-balance">
            The process of building illuminates the gaps in our thinking.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed text-balance italic font-serif">
            "Through these projects, I am constantly reminded that elegant code and beautiful design only matter if they solve the core problem. The aesthetics draw you in, but the utility makes you stay."
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-background text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-serif text-foreground mb-8">
            Have a project in mind? Let's talk.
          </h2>
          <a href="https://calendly.com/shateara-davis-proton" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-none px-10 py-7 text-sm uppercase tracking-wider font-semibold">
              Book a Call
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
